import React, { useState } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';

//GENERADO POR IA
// Hook para manejar el copiado genérico
export const useCopiarTexto = () => {
  const [copiadoId, setCopiadoId] = useState(null);
  const [isCopiando, setIsCopiando] = useState(false);

  // Función fallback para navegadores antiguos
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('✅ Fallback: Texto copiado');
        return true;
      } else {
        console.error('❌ Fallback: Error al copiar');
        return false;
      }
    } catch (err) {
      console.error('❌ Fallback: Error al ejecutar comando', err);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Función principal para copiar
  const copiarTexto = async (funcionGenerarTexto, identificador = Date.now()) => {
    try {
      setIsCopiando(true);
      console.log('🔄 Iniciando copia...');
      
      // Ejecutar la función para obtener el texto
      const textoACopiar = await funcionGenerarTexto();
      
      if (!textoACopiar || typeof textoACopiar !== 'string') {
        throw new Error('La función no retornó un texto válido');
      }
      
      console.log('📝 Texto generado:', textoACopiar.substring(0, 50) + '...');
      
      let copiado = false;
      
      // Intentar con clipboard API moderna
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(textoACopiar);
          copiado = true;
          console.log('✅ Copiado con clipboard API');
        } catch (clipboardError) {
          console.warn('⚠️ Clipboard API falló, usando fallback:', clipboardError);
          copiado = fallbackCopyTextToClipboard(textoACopiar);
        }
      } else {
        // Usar fallback directamente
        copiado = fallbackCopyTextToClipboard(textoACopiar);
      }
      
      if (copiado) {
        // Mostrar feedback visual
        setCopiadoId(identificador);
        
        // Quitar feedback después de 2 segundos
        setTimeout(() => {
          setCopiadoId(null);
        }, 2000);
        
        console.log('✅ Texto copiado al portapapeles');
        return true;
      } else {
        throw new Error('No se pudo copiar el texto');
      }
      
    } catch (error) {
      console.error('❌ Error al copiar:', error);
      return false;
    } finally {
      setIsCopiando(false);
    }
  };

  return {
    copiarTexto,
    copiadoId,
    isCopiando
  };
};

// Componente del botón genérico
export const BotonCopiar = ({ 
  funcionGenerarTexto,
  identificador = Date.now(),
  className = "",
  mostrarTexto = true,
  textoCopiado = "¡Copiado!",
  textoCopiar = "Copiar",
  textoCopiando = "Copiando...",
  iconSize = 16,
  disabled = false,
  onSuccess = null,
  onError = null
}) => {
  const { copiarTexto, copiadoId, isCopiando } = useCopiarTexto();
  
  const handleCopiar = async () => {
    if (disabled || isCopiando) return;
    
    const exito = await copiarTexto(funcionGenerarTexto, identificador);
    
    if (exito && onSuccess) {
      onSuccess();
    } else if (!exito && onError) {
      onError();
    }
  };

  const estaCopiadoEste = copiadoId === identificador;
  const estaDeshabilitado = disabled || isCopiando;

  return (
    <button
      onClick={handleCopiar}
      className={`px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium ${
        estaCopiadoEste
          ? 'bg-green-100 text-green-700 border border-green-200'
          : isCopiando
          ? 'bg-blue-100 text-blue-700 border border-blue-200'
          : estaDeshabilitado
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm active:scale-95'
      } ${className}`}
      disabled={estaDeshabilitado}
      title={
        estaCopiadoEste 
          ? textoCopiado 
          : isCopiando 
          ? textoCopiando 
          : textoCopiar
      }
    >
      {estaCopiadoEste ? (
        <>
          <Check size={iconSize} className="text-green-600" />
          {mostrarTexto && <span>{textoCopiado}</span>}
        </>
      ) : isCopiando ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
          {mostrarTexto && <span>{textoCopiando}</span>}
        </>
      ) : (
        <>
          <Copy size={iconSize} />
          {mostrarTexto && <span>{textoCopiar}</span>}
        </>
      )}
    </button>
  );
};

// Para usar con tu función específica - Versión simplificada
export const copiarConFuncion = async (funcionGenerarTexto) => {
  try {
    const texto = await funcionGenerarTexto();
    
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
    } else {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = texto;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    
    return true;
  } catch (error) {
    console.error('Error al copiar:', error);
    return false;
  }
};